import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { BallastType } from "../../../../../http/border/types/ballastTypes";
import { Ballast } from "../../../../../http/border/types/ballasts";
import { useBallastTypesApi } from "../../../../../http/border/useBallastTypes";
import { useBallastsApi } from "../../../../../http/border/useBallasts";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface BallastFormBody {
  item: AdminItem | null;
  ballastType: BallastType | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface BallastFormProps {
  onClose: () => void;
  ballast?: Ballast;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const BallastForm: FC<BallastFormProps> = ({ onClose, ballast, isDisabled, isLoading }: BallastFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const [defaultBallastTypeOptions, setDefaultBallastTypeOptions] = useState<BallastType[]>([]);

  const ballastsApi = useBallastsApi();
  const ballastTypesApi = useBallastTypesApi();
  const itemsApi = useItemsApi();
  const ballastTypeSearchTerm = useRef("");
  const ballastTypeAborter = useRef(new AbortController());
  const loadBallastTypeOptions = createLoadOptions(
    ballastTypeAborter,
    ballastTypeSearchTerm,
    ballastTypesApi.getAdminBallastTypes,
  );

  useEffect(() => {
    loadBallastTypeOptions(ballastTypeSearchTerm.current).then(setDefaultBallastTypeOptions);
  }, []);

  const onSubmit = () => async (formData: BallastFormBody) => {
    setIsSubmitting(true);

    try {
      const body = {
        ballastTypeId: formData.ballastType?.id ?? null,
        order: formData.order!,
        visibility: formData.visibility,
      };

      if (ballast) {
        await ballastsApi.updateAdminBallast(ballast.item.id, body);
      } else {
        await ballastsApi.insertAdminBallast({
          ...body,
          itemId: formData.item?.id!,
        });
      }
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<BallastFormBody>
      defaultValues={{
        item: ballast?.item ?? null,
        ballastType: ballast?.ballastType ?? null,
        order: ballast?.order ?? 50,
        visibility: ballast?.visibility ?? "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue }) => {
        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            isDisabled={isDisabled || isLoading || isSubmitting || !!ballast}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="ballastType"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.translation.ballastTypes.singular} error={fieldState.error} isOptional>
                    {({ labelId }) => (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultBallastTypeOptions}
                        isDisabled={isDisabled || isLoading || isSubmitting}
                        loadOptions={loadBallastTypeOptions}
                        isClearable
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("ballastType", value)}
                      />
                    )}
                  </FormField>
                );
              }}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default BallastForm;
